import { BillingGridDatasource } from '@/modules/billing-module/components/grid/config/datasources/billing-grid.datasource'
import { CommonFilterModel, CommonParams, ExportCommonParams } from '@/api/resources/common/shared/models/common.models'
import { AgColumnDefinition, IResponseAgGrid } from '@/shared/models/ag-grid.model'
import { GridUtils } from '@/shared/util/grid-utils'
import { billingGridInvoiceReceiptsColumnDefs } from '@/modules/billing-module/components/grid/config/column-defs/billing-grid-invoice-receipts.column-defs'
import {
    BillingBillableDeliveryNoteSearchGridRequest,
    BillingCaseSearchGridRequest,
    BillingPatientSearchGridRequest
} from '@/api/services/billing/billing.dto'
import {
    BillingSearcherCaseMode,
    BillingSearcherMode,
    BillingSearcherParams
} from '@/modules/billing-module/components/searcher/billing-searcher.model'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { StrictNullable } from '@/shared/types/app.types'
import { ReportGridRequest } from '@/api/services/common.dto'
import { AxiosResponse } from 'axios'
import { billingInvoiceReceiptService } from '@/api/services/billing/invoice-receipt/billing-invoice-receipt.service'
import {
    InvoiceReceiptStatus,
    InvoiceReceiptResponse
} from '@/api/services/billing/invoice-receipt/billing-invoice-receipt.dto'

export class BillingGridInvoiceReceiptsDatasource extends BillingGridDatasource<InvoiceReceiptResponse> {
    private readonly _category: InvoiceReceiptStatus

    constructor(laboratoryId: number, mode: InvoiceReceiptStatus) {
        super(laboratoryId)
        this._category = mode
    }

    public getColumnDefs(): AgColumnDefinition[] {
        return billingGridInvoiceReceiptsColumnDefs()
    }
    protected fetchData(filterModel: CommonFilterModel, params: CommonParams): Promise<IResponseAgGrid<InvoiceReceiptResponse>> {
        const _filterModel: CommonFilterModel = {
            ...filterModel,
            'status': GridUtils.getSetFilterModel([this._category])
        }
        return billingInvoiceReceiptService.paginator(_filterModel, params, this.laboratoryId)
    }

    public exporter(request: ReportGridRequest, params: ExportCommonParams): Promise<AxiosResponse<Blob>> {
        const filterModel: CommonFilterModel = {
            ...request.filterModel,
            'status': GridUtils.getSetFilterModel([this._category])
        }
        return billingInvoiceReceiptService.export(this.laboratoryId, { ...request, filterModel }, params)
    }

    public getSearcherModes(): BillingSearcherMode[] {
        return [
            BillingSearcherMode.PATIENT,
            BillingSearcherMode.DELIVERY_NOTE,
            BillingSearcherMode.CUSTOMER,
            BillingSearcherMode.INVOICE,
            BillingSearcherMode.CASE,
            BillingSearcherMode.INVOICE_RECEIPT
        ]
    }
    private async _patientSearcher(patientsIds: number[]): Promise<IResponseAgGrid<InvoiceReceiptResponse>> {
        const request: BillingPatientSearchGridRequest = {
            patientsIds
        }
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        return billingInvoiceReceiptService.searchPatient(this.laboratoryId, request, params)
    }
    private async _caseSearcher(caseScope: BillingSearcherCaseMode, identifierSearch: string): Promise<StrictNullable<IResponseAgGrid<InvoiceReceiptResponse>>> {
        let caseFieldName: BillingCaseSearchGridRequest['caseFieldName'] | undefined
        if (caseScope === BillingSearcherCaseMode.IDENTIFIER) {
            caseFieldName = 'casCasenumber'
        } else if (caseScope === BillingSearcherCaseMode.INSURANCE_DOCUMENT) {
            caseFieldName = 'referralNumber'
        } else if (caseScope === BillingSearcherCaseMode.INSURANCE_POLICY) {
            caseFieldName = 'insuranceNumber'
        }
        if (caseFieldName) {
            const request: BillingCaseSearchGridRequest = {
                caseFieldName,
                caseFieldValue: identifierSearch
            }
            const params: CommonParams = {
                size: PAGINATOR_BIG_PAGE_SIZE
            }
            return billingInvoiceReceiptService.searchCase(this.laboratoryId, request, params)
        }
        return null
    }
    private async _deliveryNoteSearcher(identifierSearch: string): Promise<IResponseAgGrid<InvoiceReceiptResponse>> {
        const request: BillingBillableDeliveryNoteSearchGridRequest = {
            billableDeliveryNoteLabel: identifierSearch
        }
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        return billingInvoiceReceiptService.searchBillableDeliveryNote(this.laboratoryId, request, params)
    }
    public async searcher(searcherParams: BillingSearcherParams): Promise<StrictNullable<IResponseAgGrid<InvoiceReceiptResponse>>> {
        const { mode, caseScope, patientsIds, identifierSearch } = searcherParams
        if (mode === BillingSearcherMode.PATIENT && patientsIds) {
            return this._patientSearcher(patientsIds)
        } else if (mode === BillingSearcherMode.CASE && caseScope && identifierSearch) {
            return this._caseSearcher(caseScope, identifierSearch)
        } else if (mode === BillingSearcherMode.DELIVERY_NOTE && identifierSearch) {
            return this._deliveryNoteSearcher(identifierSearch)
        } else {
            const filterModel: CommonFilterModel = {}
            if (mode === BillingSearcherMode.CUSTOMER) {
                filterModel['invoice.customer.name'] = GridUtils.getTextFilterModel('contains', identifierSearch)
            } else if (mode === BillingSearcherMode.INVOICE) {
                filterModel['invoice.label'] = GridUtils.getTextFilterModel('contains', identifierSearch)
            } else if (mode === BillingSearcherMode.INVOICE_RECEIPT) {
                filterModel['label'] = GridUtils.getTextFilterModel('contains', identifierSearch)
            }
            const params: CommonParams = {
                size: PAGINATOR_BIG_PAGE_SIZE
            }
            return billingInvoiceReceiptService.paginator(filterModel, params, this.laboratoryId)
        }
    }
}
